/**
 * Witness evidence helpers — the "Who's Who" leaf module.
 *
 * Pure functions + shared enums for the Witnesses tab (the prosecutor's roster
 * and per-person evidence panel). Citation deep-links reuse domain/sourceRef.js
 * so clicking a witness's evidence opens the global document viewer overlay
 * exactly like chat citations and the Chronology / Tensions tabs.
 *
 * Kept deliberately dependency-light and self-contained: shared enums/consts
 * live HERE (a leaf module) rather than in a context/registry file, to avoid the
 * circular-import → blank-screen trap that bites context files that loop via
 * shared hooks.
 */

import { buildEvidenceUrl, buildEvidenceLabel } from './sourceRef';

/* ── Attachment families ──
 * base.pdf / base_1.pdf / base_2.pdf are one logical document split at the
 * source (email attachments, scanner page-splits). Mirrors the backend
 * family_key (checklist_match filename_exact): strip a trailing _N from the
 * extension-less stem. Underscore-suffix only — "Exhibit 12.pdf" is a
 * distinct document, not a family member. */
export function familyKey(filename) {
  if (!filename) return '';
  const stem = String(filename).replace(/\.[^./\\]+$/, '');
  return stem.replace(/_\d{1,3}$/, '').replace(/[\s._-]+/g, ' ').trim().toLowerCase();
}

/** Collapse a citation-item list to one entry per attachment family (first
 * member wins — it carries the deep link). Items without filenames pass
 * through untouched. */
export function dedupeCitationsByFamily(items) {
  const seen = new Set();
  const out = [];
  for (const item of items || []) {
    const fk = familyKey(item?.filename);
    if (!fk) { out.push(item); continue; }
    if (seen.has(fk)) continue;
    seen.add(fk);
    out.push(item);
  }
  return out;
}

/* ── Importance — the three-tier review/triage band ── */
export const IMPORTANCE = Object.freeze({
  KEY: 'key',
  STANDARD: 'standard',
  PERIPHERAL: 'peripheral',
});

export const IMPORTANCE_LABEL = Object.freeze({
  key: 'Key',
  standard: 'Standard',
  peripheral: 'Peripheral',
});

/* Sort rank for the importance band (key first, peripheral last). Anything
 * unrecognized sorts after the three known bands. */
export const IMPORTANCE_RANK = Object.freeze({
  key: 0,
  standard: 1,
  peripheral: 2,
});

/**
 * Roster comparator: importance band (key → standard → peripheral) then name
 * (alphabetical, case-insensitive). Stable for equal keys.
 */
export function compareWitnesses(a, b) {
  const ra = IMPORTANCE_RANK[a?.importance] ?? 3;
  const rb = IMPORTANCE_RANK[b?.importance] ?? 3;
  if (ra !== rb) return ra - rb;
  const na = (a?.display_name || '').toLowerCase();
  const nb = (b?.display_name || '').toLowerCase();
  return na.localeCompare(nb);
}

/* ── Quality label — the extractor's classification of an entity ──
 * real_person → a named individual; group → a collective (e.g. "bar patrons");
 * role_reference → an unnamed role ("the driver"); junk → noise. null/absent →
 * unclassified. These are opt-in FILTER values only — nothing is auto-hidden. */
export const QUALITY_LABEL = Object.freeze({
  real_person: 'Named individual',
  group: 'Group',
  role_reference: 'Role / unnamed',
  junk: 'Junk',
  unclassified: 'Unclassified',
});

/** Human label for a quality_label value (null/absent → "Unclassified"). */
export function formatQuality(quality) {
  const key = quality || 'unclassified';
  return QUALITY_LABEL[key] || QUALITY_LABEL.unclassified;
}

/* ── Suggestions-column sort modes ──
 * Salience is the server's default order (best-first); the rest re-sort
 * client-side. Kept here (leaf module) so the tab and any future control share
 * one vocabulary. */
export const WITNESS_SORT = Object.freeze({
  SALIENCE: 'salience',
  EVIDENCE: 'evidence',
  NAME: 'name',
  TYPE: 'type',
});

// User-facing sort labels. "Relevance" = the salience score (how central a
// person is to the case: corroboration across docs + mention volume +
// substantive graph activity, weighted by role) — renamed from the jargon
// "Salience". "Evidence" = raw item count; the two differ (a booking officer is
// mentioned a lot but isn't relevant).
export const WITNESS_SORT_LABELS = Object.freeze({
  salience: 'Relevance',
  evidence: 'Evidence count',
  name: 'Name',
  type: 'Role',
});

/**
 * Comparator factory for the suggestions sort control. Salience/Evidence sort
 * descending (best/most first); Name/Type ascending. Salience preserves the
 * server's relative order as a stable tiebreak by leaning on the array's
 * incoming order (callers should pass a stable .sort()).
 */
export function compareWitnessesBy(mode) {
  switch (mode) {
    case WITNESS_SORT.EVIDENCE:
      return (a, b) =>
        (b?.evidence_count || 0) - (a?.evidence_count || 0)
        || (a?.display_name || '').toLowerCase().localeCompare((b?.display_name || '').toLowerCase());
    case WITNESS_SORT.NAME:
      return (a, b) =>
        (a?.display_name || '').toLowerCase().localeCompare((b?.display_name || '').toLowerCase());
    case WITNESS_SORT.TYPE:
      return (a, b) =>
        formatRole(a).localeCompare(formatRole(b))
        || (a?.display_name || '').toLowerCase().localeCompare((b?.display_name || '').toLowerCase());
    case WITNESS_SORT.SALIENCE:
    default:
      // Higher salience first; treat missing salience as -Infinity so it sinks.
      return (a, b) => {
        const sa = typeof a?.salience === 'number' ? a.salience : -Infinity;
        const sb = typeof b?.salience === 'number' ? b.salience : -Infinity;
        return sb - sa
          || (a?.display_name || '').toLowerCase().localeCompare((b?.display_name || '').toLowerCase());
      };
  }
}

/* ── Review state — the human-review gate (suggested → accepted/dismissed) ── */
export const REVIEW_STATE = Object.freeze({
  SUGGESTED: 'suggested',
  ACCEPTED: 'accepted',
  DISMISSED: 'dismissed',
});

/* ── Dedup-confidence cue on suggested roster cards ── */
export const DEDUP_CONFIDENCE_LABEL = Object.freeze({
  high: 'high confidence',
  medium: 'medium confidence',
  low: 'low confidence',
});

/**
 * Evidence-rail icon glyphs.
 *
 * The dossier's `evidence.registry` is the authoritative rail config — it ships
 * an `icon` STRING per bucket ('quote' | 'video' | 'document' | 'calendar').
 * We map that string to a glyph here so the rail renders from the backend's
 * registry (never a hardcoded bucket list). `all` is the synthetic
 * everything-bucket the tab prepends.
 */
export const EVIDENCE_ICON = Object.freeze({
  quote: '💬',
  video: '🎬',
  document: '📄',
  calendar: '📅',
  all: '🗂',
});

/** Glyph for a registry icon key, falling back to the generic document glyph. */
export function evidenceGlyph(iconKey) {
  return EVIDENCE_ICON[iconKey] || EVIDENCE_ICON.document;
}

/** Human label for a role: prefer the server's role_display, else titleize. */
export function formatRole(witness) {
  if (!witness) return '';
  if (witness.role_display) return witness.role_display;
  const r = witness.role;
  if (!r) return '';
  return String(r).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Human label for an importance band ('Key' / 'Standard' / 'Peripheral'). */
export function formatImportance(importance) {
  return IMPORTANCE_LABEL[importance] || (importance ? String(importance) : '');
}

/**
 * Build a SourceRef for a claim/document/event evidence item, or null.
 *
 * Mirrors chronologyEntry.entrySourceRef: the per-bucket items carry a
 * `document_id` (+ optional `source_chunk_id` / `time_start` for media) and a
 * verbatim quote to seed the viewer highlight. Events fall back to their first
 * source document.
 */
export function evidenceSourceRef(item) {
  if (!item) return null;
  // Event items nest their source docs; use the first one.
  if (item.kind === 'event') {
    const src = (item.source_documents || [])[0];
    if (!src || !src.document_id) return null;
    return {
      document_id: src.document_id,
      filename: src.filename,
      snippet: item.description || '',
      kind: 'none',
      verified: true,
    };
  }
  if (!item.document_id) return null;
  return {
    document_id: item.document_id,
    chunk_id: item.source_chunk_id,
    filename: item.filename,
    // Media items deep-link to a timestamp when the transcript carries one.
    timestamp: item.time_start != null ? item.time_start : undefined,
    snippet: item.supporting_quote || item.claim_text || '',
    kind: 'none',
    verified: true,
  };
}

/** Citation href + label for an evidence item (or null if no linkable source). */
export function evidenceCitation(item, pathname) {
  const ref = evidenceSourceRef(item);
  if (!ref) return null;
  const href = buildEvidenceUrl(ref, pathname);
  if (!href) return null;
  return { href, label: buildEvidenceLabel(ref) || item.filename || 'Open source' };
}

/**
 * Pinpoint deep-link for a reference (a bound fragment from
 * list_member_fragments) — opens the document viewer scrolled to + highlighting
 * where this person is *named*, not just the filename.
 *
 * The anchor is the entity's `source_chunk_id` (the chunk the name was
 * extracted from); the person's name rides along as the snippet so the viewer
 * can narrow the highlight within the chunk (and as the legacy `q` fallback for
 * chunks without a stored location). Chunk-granular today — lands on the
 * paragraph the name appears in, not the exact word.
 *
 * Returns { href, label } or null when the reference has no linkable document.
 */
export function referenceCitation(fragment, pathname) {
  if (!fragment || !fragment.document_id) return null;
  const ref = {
    document_id: fragment.document_id,
    chunk_id: fragment.source_chunk_id || undefined,
    filename: fragment.filename,
    // The surface form that actually matched in the chunk (the fuzzy `q`
    // fallback for chunks whose stored location can't compose the range),
    // falling back to the canonical name.
    snippet: fragment.pinpoint_quote || fragment.name || '',
    // Chunk-relative [start, end) of the name within its chunk — lets the
    // viewer highlight the name itself, not the whole paragraph. buildEvidenceUrl
    // validates the shape and only emits ?cr= when it's a real 2-int range.
    cited_char_range: fragment.cited_char_range || undefined,
  };
  const href = buildEvidenceUrl(ref, pathname);
  if (!href) return null;
  return { href, label: buildEvidenceLabel(ref) || fragment.filename || 'Open document' };
}

/** Display name for one participant (string or {canonical_name/name} shape). */
export function participantName(p) {
  if (!p) return '';
  if (typeof p === 'string') return p;
  return p.canonical_name || p.name || '';
}

/** Lowercased, space-normalized verb for an event's witness role on it. */
export function eventRole(item) {
  const r = item?.role;
  return r ? String(r).replace(/_/g, ' ').toLowerCase() : '';
}
